import { Error } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate, useParams } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { CustomerSelectRow } from "~/features/quote/components/quote-details/components/customer-select-row/customer-select-row.tsx";
import { BillingCustomerDropdown } from "~/features/quote/components/shipment-details/components/billing-customer-dropdown/billing-customer-dropdown.tsx";
import { ItemDetails } from "~/features/quote/components/shipment-details/components/shipment-item-details/shipment-item-details.tsx";
import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useAddressBookAccessorials } from "~/features/quote/hooks/use-address-book-accessorials.ts";
import {
	useFormHandlers,
	useGetCustomerDetails,
	useQuoteDetailsPageLoadQueries,
	useQuoteLoader,
	useUserDefaultsFetcher,
} from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { useSubmitUpdatedQuote } from "~/features/quote/hooks/use-shipment-summary-hooks.tsx";
import { useTemplateModals } from "~/features/quote/hooks/use-template-modals.ts";
import { hasAddressFieldOverLimit } from "~/features/quote/utils/has-address-field-over-limit.ts";
import { getLtlFallbackMessage } from "~/features/quote/utils/ltl-error-handler.ts";
import { mapPostQuoteCommoditiesToHandlingUnits } from "~/features/quote/utils/map-post-quote-commodity-to-handling-unit.ts";
import { AddEditTemplateModal } from "~/features/templates/components/add-edit-template-modal/add-edit-template-modal.tsx";
import { TemplatesModal } from "~/features/templates/components/templates-modal/templates-modal.tsx";
import { QuoteStatus } from "~/shared/constants/quote-status.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import { useSharedListsAccess } from "~/shared/hooks/use-shared-lists-access.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type { QuoteResponse } from "~/shared/schemas/quote-schema.ts";
import { getBaseUrl } from "~/shared/utils/get-base-url.ts";

import { useLtlTenderFlow } from "../../hooks/use-ltl-tender-flow.ts";
import { useTenderValidation } from "../../hooks/use-tender-validation.ts";
import { TenderAdditionalDetails } from "../tender-additional-details/tender-additional-details.tsx";
import { TenderCommodities } from "../tender-commodities/tender-commodities.tsx";
import { TenderHazardousMaterialsDetails } from "../tender-hazardous-materials-details/tender-hazardous-materials-details.tsx";
import { TenderPickupDeliveryDetails } from "../tender-pickup-delivery-details/tender-pickup-delivery-details.tsx";
import { TenderSpecialInstructions } from "../tender-special-instructions/tender-special-instructions.tsx";
import styles from "./tender-shipment-details.module.css";

type TenderShipmentDetailsProps = {
	readonly isRepairMode?: boolean;
	readonly disableSubmitButton?: boolean;
	readonly onPaymentRequired?: (quote: QuoteResponse) => void;
	readonly onTenderError?: (message: string) => void;
	readonly onTenderedWithDocs?: () => void;
	readonly onVendorTenderedToRep?: () => void;
	readonly onSaveSuccess?: () => void;
	readonly onSaveError?: (message: string) => void;
	readonly carrierSelectionRoute?: string;
};

export const TenderShipmentDetails = ({
	isRepairMode = false,
	disableSubmitButton = false,
	onPaymentRequired,
	onTenderError,
	onTenderedWithDocs,
	onVendorTenderedToRep,
	onSaveSuccess,
	onSaveError,
	carrierSelectionRoute = routes.ltlShipmentCarrierSelection,
}: TenderShipmentDetailsProps) => {
	const baseUrl = getBaseUrl();
	const { mergeResponseIntoQuoteState, quoteState } =
		useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { isInternal } = useDeploymentType();
	const { isVendor } = useIsVendor();
	const { submitUpdatedQuote, isLoading: isSaving } = useSubmitUpdatedQuote();
	const { canManageSharedLists } = useSharedListsAccess();
	const {
		validationErrors,
		validate,
		clearFieldError,
		clearItemFieldError,
		hasFieldError,
	} = useTenderValidation();
	const { quoteId } = useParams<{ quoteId: string }>();
	const navigate = useNavigate();
	const { data: pageData } = useQuoteDetailsPageLoadQueries();
	const { allAccessorials } = useAddressBookAccessorials();
	const shouldPerformQuoteOperations = useMemo(
		() => Boolean(quoteId),
		[quoteId],
	);
	const { isLoadingQuote, isCopyNavigation } = useQuoteLoader({
		includeCustomerData: false,
		includeFormReset: !shouldPerformQuoteOperations,
	});
	const [customerCleared, setCustomerCleared] = useState(false);
	const [hasAttemptedTender, setHasAttemptedTender] = useState(false);
	const { getChangeHandler } = useFormHandlers();
	const customerId = quoteState?.customer?.id ?? 0;
	const { isLoading: customerDetailsLoading } =
		useGetCustomerDetails(customerId);
	const isParentLoading = customerDetailsLoading || isLoadingQuote;
	const { reapplyDefaults } = useUserDefaultsFetcher(
		quoteState.customer?.id,
		Boolean(!canManageSharedLists || isCopyNavigation),
	) as { reapplyDefaults: () => void };
	const {
		showTemplatesModal,
		toggleTemplatesModal,
		handleTemplateSelect,
		showSaveTemplateModal,
		saveTemplateQuoteSnapshot,
		saveTemplateAddressSnapshot,
		handleOpenSaveTemplateModal,
		handleCloseSaveTemplateModal,
		handleApplySavedTemplate,
		handleClearTemplate,
	} = useTemplateModals({ pageData, allAccessorials, reapplyDefaults });

	const hasHazmat = useMemo(
		() => quoteState.items.some((item) => item.isHazmat),
		[quoteState.items],
	);

	const validationErrorCount = useMemo(() => {
		const itemFieldCount = Object.values(validationErrors.items).reduce(
			(total, fieldSet) => total + fieldSet.size,
			0,
		);
		return validationErrors.fields.size + itemFieldCount;
	}, [validationErrors]);

	const isAnyFieldOverLimit = hasAddressFieldOverLimit(
		selectedAddressBookItems,
	);

	const { submitLtlTenderFlow, isLoading: isTendering } = useLtlTenderFlow();

	// Blocks duplicate submits fired before React commits the pending state,
	// e.g. a held Enter key repeating on the focused button.
	const inFlightRef = useRef(false);

	// Once the user has attempted to tender, keep validation in sync with the
	// form state. This clears stale "invalid" highlighting on fields that get
	// populated after the fact (e.g. when a template is applied).
	useEffect(() => {
		if (!hasAttemptedTender) return;
		validate(quoteState, isInternal, selectedAddressBookItems, isVendor);
	}, [
		hasAttemptedTender,
		validate,
		quoteState,
		isInternal,
		isVendor,
		selectedAddressBookItems,
	]);

	const runTenderFlow = useCallback(async () => {
		setHasAttemptedTender(true);
		if (!validate(quoteState, isInternal, selectedAddressBookItems, isVendor)) {
			return;
		}

		const result = await submitLtlTenderFlow();

		if (!result.success) {
			onTenderError?.(result.error);
			return;
		}

		// Tender succeeded, so the validation that ran above passed. Clear the
		// attempted-tender flag now to stop the sync effect from re-validating
		// when the post-tender modal closes and resets the form — otherwise the
		// freshly-cleared (empty) form would light up every field's error state.
		setHasAttemptedTender(false);

		const quoteData = result.data;
		mergeResponseIntoQuoteState({
			id: quoteData.id,
			carrierPrices: quoteData.carrierPrices as CarrierPrice[],
			items: mapPostQuoteCommoditiesToHandlingUnits(quoteData.quoteCommodities),
			eTag: quoteData.eTag,
			createdDate: quoteData.createdDate,
			expirationDate: quoteData.expirationDate,
			publicGuid: quoteData.publicGuid,
			insuranceApplyType: quoteData.insuranceApplyTypeId,
			insuranceApplyTypeId: quoteData.insuranceApplyTypeId,
			insuranceProductType: null,
			insuranceProductTypeId: quoteData.insuranceProductTypeId,
			insuranceValue: quoteData.insuranceValue,
			hasRoutingGuideMatch: quoteData.hasRoutingGuideMatch ?? false,
			selectedCarrierPriceId: quoteData.selectedCarrierPriceId,
			poNumber: quoteData.poNumber,
		});

		// A pre-paid external customer must pay (ACH/credit card) before the
		// shipment can finish tendering. When routing-guide auto-select tenders
		// during this step, the backend returns PaymentRequired instead of a
		// completed tender — even if a carrier and PO are present. This check must
		// run before the shipping-docs branch below so a prepaid shipment is never
		// shown as fully tendered. Vendors are NOT allowed to prepay, so a
		// PaymentRequired response is a hard stop for them — they hand off to their
		// LTL Account Rep instead. Only non-vendor external users enter the prepay
		// flow.
		if (!isInternal && quoteData.statusLevel === QuoteStatus.PaymentRequired) {
			if (isVendor) {
				onVendorTenderedToRep?.();
				return;
			}
			onPaymentRequired?.(quoteData);
			return;
		}

		// The tender response decides the next step for every user:
		//  - When it already includes a booked carrier and PO number the shipment
		//    is fully tendered, so all users get the existing shipping-docs modal
		//    and the flow ends here.
		//  - Without a PO number, vendors are handed off to their LTL Account
		//    Representative via a dedicated modal, while internal/external users
		//    continue to the carrier-selection step to choose a carrier.
		const hasShippingDocs =
			quoteData.selectedCarrierPriceId != null && quoteData.poNumber != null;

		if (hasShippingDocs) {
			onTenderedWithDocs?.();
			return;
		}

		if (isVendor) {
			onVendorTenderedToRep?.();
			return;
		}

		navigate(carrierSelectionRoute.replace(":quoteId", String(quoteData.id)), {
			replace: true,
			state: { fromCreate: true },
		});
	}, [
		carrierSelectionRoute,
		isInternal,
		isVendor,
		mergeResponseIntoQuoteState,
		navigate,
		onPaymentRequired,
		onTenderError,
		onTenderedWithDocs,
		onVendorTenderedToRep,
		quoteState,
		selectedAddressBookItems,
		submitLtlTenderFlow,
		validate,
	]);

	const handleSubmit = useCallback(async () => {
		if (inFlightRef.current) {
			return;
		}
		inFlightRef.current = true;
		try {
			await runTenderFlow();
		} finally {
			inFlightRef.current = false;
		}
	}, [runTenderFlow]);

	const handleSave = useCallback(async () => {
		if (inFlightRef.current) {
			return;
		}
		inFlightRef.current = true;
		try {
			const result = await submitUpdatedQuote();
			if (result.status === 200) {
				onSaveSuccess?.();
				return;
			}
			onSaveError?.(result.message ?? getLtlFallbackMessage(isInternal));
		} finally {
			inFlightRef.current = false;
		}
	}, [isInternal, onSaveError, onSaveSuccess, submitUpdatedQuote]);

	// Show the global overlay spinner while tendering, and while an existing
	// quote is being fetched on /ltl-shipment/{quoteId} so the user sees a
	// loading state instead of an empty form before the quote populates.
	const isBusy = isTendering || isSaving;
	useAutoSpinner(isBusy || isLoadingQuote);

	return (
		<div className={styles.tenderShipmentDetailsWrapper}>
			{isRepairMode ? (
				<div>{quoteState.customer.name}</div>
			) : (
				<CustomerSelectRow
					customerCleared={customerCleared}
					getChangeHandler={getChangeHandler}
					hideTemplates={isVendor}
					isParentLoading={isParentLoading}
					leadingContent={isInternal ? undefined : <BillingCustomerDropdown />}
					onClearTemplate={handleClearTemplate}
					onClearValidation={() => clearFieldError("customer")}
					onToggleTemplatesModal={toggleTemplatesModal}
					redirectRoute={routes.ltlShipment}
					setCustomerCleared={setCustomerCleared}
					showValidationError={hasFieldError("customer")}
				/>
			)}

			{isInternal || isRepairMode ? <BillingCustomerDropdown /> : null}

			<div className={styles.tenderShipmentRow}>
				<TenderPickupDeliveryDetails
					hasFieldError={hasFieldError}
					hideAddressBook={!canManageSharedLists}
					isRepairMode={isRepairMode}
					onClearFieldError={clearFieldError}
					type={"pickup"}
				/>
				<TenderPickupDeliveryDetails
					hasFieldError={hasFieldError}
					hideAddressBook={!canManageSharedLists}
					isRepairMode={isRepairMode}
					onClearFieldError={clearFieldError}
					type={"delivery"}
				/>
			</div>

			{isRepairMode ? (
				<ItemDetails
					showHazmatDetails
					itemValidationErrors={validationErrors.items}
					items={quoteState.items}
					onClearItemFieldError={clearItemFieldError}
				/>
			) : (
				<TenderCommodities
					hideSaveCommodity={!canManageSharedLists}
					hideSavedCommodities={!canManageSharedLists}
					itemValidationErrors={validationErrors.items}
					onClearItemFieldError={clearItemFieldError}
					requireClass={!isVendor}
				/>
			)}

			<div className={styles.tenderShipmentRow}>
				<TenderAdditionalDetails
					hasFieldError={hasFieldError}
					isRepairMode={isRepairMode}
					onClearFieldError={clearFieldError}
				/>
			</div>

			<div className={styles.tenderShipmentRow}>
				{hasHazmat ? (
					<TenderHazardousMaterialsDetails
						hasFieldError={hasFieldError}
						isRequired={!isVendor}
						onClearFieldError={clearFieldError}
					/>
				) : null}
				<TenderSpecialInstructions hideSaveInstructions={isVendor} />
			</div>

			<div className={styles.tenderShipmentActions}>
				{validationErrorCount > 0 ? (
					<div
						aria-live={"assertive"}
						className={styles.tenderValidationSummary}
						role={"alert"}
					>
						<Error>
							{validationErrorCount === 1
								? "1 field needs your attention before you can tender."
								: `${validationErrorCount} fields need your attention before you can tender.`}
						</Error>
					</div>
				) : null}
				<div className={styles.termsAndActions}>
					<div>
						<div className={styles.termsOfServiceHeader}>Terms of Service</div>
						<p>
							By tendering a shipment to TQL, you acknowledge and understand
							TQL&apos;s LTL terms and conditions and knowingly, voluntarily and
							willfully agree to the terms and conditions.
						</p>
						<p>
							<a
								className={"tql-link"}
								href={`${baseUrl}/app/ltl/data/General_TermsConditions.pdf`}
								rel={"noopener noreferrer"}
								tabIndex={-1}
								target={"_blank"}
							>
								Terms and Conditions
							</a>
						</p>
					</div>
					<div
						className={styles.tenderShipmentActionButtons}
						data-testid={"tender-footer-actions"}
					>
						{isVendor || isRepairMode ? null : (
							<div>
								<Button onClick={handleOpenSaveTemplateModal}>
									Save as Template
								</Button>
							</div>
						)}
						{isRepairMode ? (
							<div>
								<Button
									loading={isBusy}
									onClick={() => void handleSave()}
									tabIndex={-1}
								>
									Save
								</Button>
							</div>
						) : (
							<div>
								<Button
									disabled={disableSubmitButton || isAnyFieldOverLimit}
									formAction={"submit"}
									loading={isBusy}
									onClick={() => {
										void handleSubmit();
									}}
									variant={"primary"}
								>
									Tender
								</Button>
							</div>
						)}
					</div>
				</div>
			</div>

			{showSaveTemplateModal && saveTemplateQuoteSnapshot ? (
				<AddEditTemplateModal
					autoApplyOnSave={false}
					initialQuoteState={saveTemplateQuoteSnapshot}
					initialSelectedAddressBookItems={saveTemplateAddressSnapshot}
					onApplyTemplate={handleApplySavedTemplate}
					toggleModal={handleCloseSaveTemplateModal}
				/>
			) : null}
			{showTemplatesModal ? (
				<TemplatesModal
					customerId={quoteState.customer?.id}
					onClose={toggleTemplatesModal}
					onSelect={handleTemplateSelect}
				/>
			) : null}
		</div>
	);
};
